import { type ExportFormat, i18nKeyForError, parseApiError } from '@features/allocation';
import { Button, Modal, RadioGroup, RadioItem, useToast } from '@ntconsult/aluma-react';
import { Download } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectExport } from '../hooks/useExportProjectList';
import type { ProjectExportScope } from '../types/ProjectList';

type ProjectExportDialogProps = {
  open: boolean;
  onClose: () => void;
  exportMutation: ProjectExport;
  /** Projetos na página que a tela mostra. */
  pageCount: number;
  /** Projetos de todas as páginas, já com os filtros. */
  totalCount: number;
  hasActiveFilters: boolean;
};

/**
 * O arquivo sempre segue os filtros, a ordem e as colunas da tela. Aqui só se escolhe o formato e
 * se vai a página atual ou todas as páginas; com uma página só, não há o que escolher.
 */
export function ProjectExportDialog({
  open,
  onClose,
  exportMutation,
  pageCount,
  totalCount,
  hasActiveFilters,
}: ProjectExportDialogProps) {
  const { t } = useTranslation(['projetos', 'alocacao', 'common']);
  const { toast } = useToast();
  const titleId = useId();
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [scope, setScope] = useState<ProjectExportScope>('all');

  const singlePage = totalCount <= pageCount;

  const confirm = () =>
    exportMutation.mutate(
      { format, scope: singlePage ? 'all' : scope },
      {
        onSuccess: () => {
          toast.success({ title: t('alocacao:exportacao.sucesso') });
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          toast.danger({
            title: t((apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never),
          });
        },
      },
    );

  return (
    <Modal open={open} onClose={onClose} size="md" ariaLabelledBy={titleId}>
      <Modal.Header
        titleId={titleId}
        title={t('projetos:exportacao.titulo')}
        dismissLabel={t('common:close')}
        onClose={onClose}
      />
      <Modal.Body>
        <div className="flex flex-col gap-5">
          <p className="text-sm text-foreground-muted">{t('projetos:exportacao.descricao')}</p>

          {singlePage ? (
            <p className="text-sm text-foreground">
              {t('projetos:exportacao.unicaPagina', { count: totalCount })}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <span id={`${titleId}-scope`} className="text-sm font-medium text-foreground">
                {t('projetos:exportacao.escopo')}
              </span>
              <RadioGroup
                name="project-export-scope"
                aria-labelledby={`${titleId}-scope`}
                value={scope}
                onValueChange={(value) => setScope(value as ProjectExportScope)}
              >
                <RadioItem
                  value="page"
                  label={t('projetos:exportacao.paginaAtual', { count: pageCount })}
                />
                <RadioItem
                  value="all"
                  label={t(
                    hasActiveFilters
                      ? 'projetos:exportacao.todosFiltrados'
                      : 'projetos:exportacao.todos',
                    { count: totalCount },
                  )}
                />
              </RadioGroup>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span id={`${titleId}-format`} className="text-sm font-medium text-foreground">
              {t('projetos:exportacao.formato')}
            </span>
            <RadioGroup
              name="project-export-format"
              aria-labelledby={`${titleId}-format`}
              orientation="horizontal"
              value={format}
              onValueChange={(value) => setFormat(value as ExportFormat)}
            >
              <RadioItem value="xlsx" label={t('alocacao:exportacao.excel')} />
              <RadioItem value="csv" label={t('alocacao:exportacao.csv')} />
            </RadioGroup>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button
          leftIcon={<Download size={14} />}
          loading={exportMutation.isPending}
          onClick={confirm}
        >
          {t('alocacao:exportacao.botao')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
